import React, { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Users,
  MapPin,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { AUTH_URL } from "../../constants";
import { useAuth } from "../../contexts/AuthContext";
import RichText from "../common/RichText";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function EventsAdmin() {
  const { token } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [includePast, setIncludePast] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState("start_at");
  const [sortAsc, setSortAsc] = useState(true);
  // Descriptions are free text and often long — they get an expandable row
  // rather than a column, so the RSVP table stays scannable.
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    fetch(`${AUTH_URL}/admin/facilities`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : d.facilities || [];
        setFacilities(list);
        if (list.length > 0) setFacilityId(list[0].facility_id);
      })
      .catch(() => {});
  }, [token]);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${AUTH_URL}/admin/events?facility_id=${facilityId}&include_past=${includePast}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Failed to load events");
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [facilityId, includePast, token]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleDetails = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSort = (field) => {
    if (sortField === field) setSortAsc((a) => !a);
    else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sorted = [...events].sort((a, b) => {
    const va = a[sortField] ?? "";
    const vb = b[sortField] ?? "";
    const cmp = String(va).localeCompare(String(vb), undefined, {
      numeric: true,
    });
    return sortAsc ? cmp : -cmp;
  });

  const totalGoing = events.reduce((s, e) => s + (e.going_count || 0), 0);
  const totalMaybe = events.reduce((s, e) => s + (e.maybe_count || 0), 0);
  const totalCheckins = events.reduce((s, e) => s + (e.checkin_count || 0), 0);

  const SortIcon = ({ field }) =>
    sortField === field ? (
      sortAsc ? (
        <ChevronUp size={12} className="inline ml-1" />
      ) : (
        <ChevronDown size={12} className="inline ml-1" />
      )
    ) : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Events</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            RSVP summary by facility
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4 flex-wrap">
        <select
          value={facilityId}
          onChange={(e) => setFacilityId(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {facilities.map((f) => (
            <option key={f.facility_id} value={f.facility_id}>
              {f.display_name || f.facility_id}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includePast}
            onChange={(e) => setIncludePast(e.target.checked)}
            className="rounded"
          />
          Include past events
        </label>
      </div>

      {/* Summary cards */}
      {events.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {[
            {
              label: "Total Going",
              value: totalGoing,
              color: "text-emerald-600",
              bg: "bg-emerald-50 border-emerald-200",
            },
            {
              label: "Total Maybe",
              value: totalMaybe,
              color: "text-amber-600",
              bg: "bg-amber-50 border-amber-200",
            },
            {
              label: "Check-ins",
              value: totalCheckins,
              color: "text-blue-600",
              bg: "bg-blue-50 border-blue-200",
            },
          ].map(({ label, value, color, bg }) => (
            <div key={label} className={`rounded-xl border p-4 ${bg}`}>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {label}
              </p>
              <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      {!error && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  { field: "title", label: "Event" },
                  { field: "start_at", label: "Date" },
                  { field: "location_name", label: "Location" },
                  { field: "going_count", label: "Going" },
                  { field: "maybe_count", label: "Maybe" },
                  { field: "not_going_count", label: "Declined" },
                  { field: "checkin_count", label: "Checked In" },
                ].map(({ field, label }) => (
                  <th
                    key={field}
                    onClick={() => toggleSort(field)}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                  >
                    {label}
                    <SortIcon field={field} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    No events found.
                  </td>
                </tr>
              )}
              {!loading &&
                sorted.map((ev) => {
                  const totalRsvp =
                    (ev.going_count || 0) +
                    (ev.maybe_count || 0) +
                    (ev.not_going_count || 0);
                  const isOpen = expanded.has(ev.id);
                  return (
                    <React.Fragment key={ev.id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Calendar
                              size={14}
                              className="text-gray-400 flex-shrink-0"
                            />
                            <div>
                              <p className="font-medium text-gray-900 leading-snug">
                                {ev.title}
                              </p>
                              {ev.event_type && (
                                <p className="text-xs text-gray-400 capitalize">
                                  {ev.event_type}
                                </p>
                              )}
                              {ev.agenda && (
                                <button
                                  onClick={() => toggleDetails(ev.id)}
                                  aria-expanded={isOpen}
                                  className="mt-0.5 flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-700"
                                >
                                  {isOpen ? "Hide details" : "Details"}
                                  {isOpen ? (
                                    <ChevronUp size={11} />
                                  ) : (
                                    <ChevronDown size={11} />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {fmt(ev.start_at)}
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-[180px]">
                          {ev.location_name ? (
                            <div className="flex items-center gap-1 min-w-0">
                              <MapPin
                                size={12}
                                className="text-gray-400 flex-shrink-0"
                              />
                              <span className="truncate">
                                {ev.location_name}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`font-semibold ${ev.going_count > 0 ? "text-emerald-600" : "text-gray-300"}`}
                          >
                            {ev.going_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`font-semibold ${ev.maybe_count > 0 ? "text-amber-600" : "text-gray-300"}`}
                          >
                            {ev.maybe_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`font-semibold ${ev.not_going_count > 0 ? "text-rose-500" : "text-gray-300"}`}
                          >
                            {ev.not_going_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`font-semibold ${ev.checkin_count > 0 ? "text-blue-600" : "text-gray-300"}`}
                            >
                              {ev.checkin_count || 0}
                            </span>
                            {totalRsvp > 0 && ev.checkin_count > 0 && (
                              <span className="text-xs text-gray-400">
                                / {ev.going_count || 0}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-gray-50/60">
                          <td colSpan={7} className="px-4 pb-4 pt-0">
                            {/* ICS feeds put HTML in here — RichText sanitizes
                              it and forces links out to a new tab. */}
                            <RichText
                              text={ev.agenda}
                              className="max-w-3xl text-sm leading-relaxed text-gray-600"
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
